import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { ArrowLeft, Flame, Loader2, Save } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function buildTimeOptions() {
    const opts = [];
    for (let h = 8; h <= 20; h++) {
        for (const m of ["00", "30"]) {
            if (h === 20 && m === "30") break;
            opts.push(`${String(h).padStart(2, "0")}:${m}`);
        }
    }
    return opts;
}
const TIME_OPTIONS = buildTimeOptions();

function formatTime12(t) {
    const [hh, mm] = t.split(":").map(Number);
    const period = hh >= 12 ? "PM" : "AM";
    const h12 = hh % 12 === 0 ? 12 : hh % 12;
    return `${h12}:${String(mm).padStart(2, "0")} ${period}`;
}

const PromoRow = ({ row, onSaved }) => {
    const [active, setActive] = useState(row.promo?.active ?? false);
    const [price, setPrice] = useState(row.promo?.promo_price ?? "");
    const [start, setStart] = useState(row.promo?.start ?? "08:00");
    const [end, setEnd] = useState(row.promo?.end ?? "10:00");
    const [saving, setSaving] = useState(false);

    const save = async () => {
        const numPrice = parseInt(price, 10);
        if (!numPrice || numPrice <= 0) {
            toast.error("Ingresa un precio promocional válido");
            return;
        }
        if (start >= end) {
            toast.error("La hora de inicio debe ser menor a la de fin");
            return;
        }
        setSaving(true);
        try {
            await axios.put(
                `${API}/admin/promos/${row.procedure_id}`,
                { promo_price: numPrice, start, end, active },
                { withCredentials: true }
            );
            toast.success(
                active
                    ? `Promoción activa: ${row.name} a $${numPrice}`
                    : "Promoción guardada (pausada)"
            );
            onSaved();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "No se pudo guardar la promoción");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card
            className={`rounded-2xl border p-5 transition-colors ${
                active ? "border-amber-300 bg-amber-50/50" : "border-border bg-card"
            }`}
            data-testid={`promo-row-${row.procedure_id}`}
        >
            <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                    <p className="font-heading text-base font-medium leading-tight">{row.name}</p>
                    <p className="text-sm text-muted-foreground mt-0.5">
                        Precio normal: ${row.price?.toLocaleString("es-MX")} MXN
                    </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <span
                        className={`text-xs font-semibold uppercase tracking-wider ${
                            active ? "text-amber-700" : "text-muted-foreground"
                        }`}
                    >
                        {active ? "Activa" : "Pausada"}
                    </span>
                    <Switch
                        checked={active}
                        onCheckedChange={setActive}
                        data-testid={`promo-switch-${row.procedure_id}`}
                    />
                </div>
            </div>

            <div className="flex flex-wrap items-end gap-3">
                <div>
                    <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium block mb-1">
                        Precio promo ($)
                    </label>
                    <Input
                        type="number"
                        min={1}
                        value={price}
                        onChange={(e) => setPrice(e.target.value)}
                        placeholder="220"
                        className="h-10 w-28 rounded-xl"
                        data-testid={`promo-price-${row.procedure_id}`}
                    />
                </div>
                <div>
                    <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium block mb-1">
                        Desde
                    </label>
                    <select
                        value={start}
                        onChange={(e) => setStart(e.target.value)}
                        className="h-10 rounded-xl border border-input bg-background px-2 text-sm"
                        data-testid={`promo-start-${row.procedure_id}`}
                    >
                        {TIME_OPTIONS.slice(0, -1).map((t) => (
                            <option key={t} value={t}>{formatTime12(t)}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium block mb-1">
                        Hasta
                    </label>
                    <select
                        value={end}
                        onChange={(e) => setEnd(e.target.value)}
                        className="h-10 rounded-xl border border-input bg-background px-2 text-sm"
                        data-testid={`promo-end-${row.procedure_id}`}
                    >
                        {TIME_OPTIONS.slice(1).map((t) => (
                            <option key={t} value={t}>{formatTime12(t)}</option>
                        ))}
                    </select>
                </div>
                <Button
                    onClick={save}
                    disabled={saving}
                    className="h-10 rounded-xl"
                    data-testid={`promo-save-${row.procedure_id}`}
                >
                    {saving ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                        <>
                            <Save className="h-4 w-4 mr-1.5" />
                            Guardar
                        </>
                    )}
                </Button>
            </div>
            {active && price && (
                <p className="text-xs text-amber-700 font-medium mt-3">
                    🔥 Los clientes verán ${price} en los horarios de {formatTime12(start)} a{" "}
                    {formatTime12(end)} (citas que inician antes de las {formatTime12(end)}).
                </p>
            )}
        </Card>
    );
};

export default function AdminPromos({ onBack }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            const res = await axios.get(`${API}/admin/promos`, { withCredentials: true });
            setRows(res.data || []);
        } catch (err) {
            toast.error("No se pudieron cargar las promociones");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    return (
        <div className="min-h-screen bg-background">
            <main className="max-w-3xl mx-auto px-5 sm:px-8 py-10">
                <Button
                    variant="outline"
                    onClick={onBack}
                    className="rounded-2xl mb-6"
                    data-testid="promos-back-btn"
                >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Volver a citas
                </Button>

                <div className="mb-8">
                    <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-2 inline-flex items-center gap-2">
                        <Flame className="h-3.5 w-3.5 text-amber-600" />
                        Promociones
                    </p>
                    <h1 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight">
                        Precios promocionales
                    </h1>
                    <p className="text-muted-foreground mt-2">
                        Activa un precio especial por procedimiento en horarios de baja demanda.
                        Los cambios aplican de inmediato en la página de reservas.
                    </p>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-16 text-muted-foreground">
                        <Loader2 className="h-5 w-5 animate-spin mr-2" />
                        Cargando...
                    </div>
                ) : (
                    <div className="space-y-4" data-testid="promos-list">
                        {rows.map((r) => (
                            <PromoRow key={r.procedure_id} row={r} onSaved={load} />
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
}
